<script lang="ts">
  import axios from 'axios'
  import PubSub from 'pubsub-js'
  import { onMount } from 'svelte'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import PageHome from '@/components/appDriver/PageHome.svelte'
  import LoadingOverlay from '@/components/appDriver/driverTax/LoadingOverlay.svelte'
  import { currentPage } from '@/components/appDriver/store'
  import { showErrorMessage } from '@/plugins/util'
  import { getAdsRegistration, registerAdsInterest } from '@/components/appDriver/adsRegistration/service'
  import { adsRegistrationText as text } from '@/components/appDriver/adsRegistration/translations'
  import type { AdsRegistration } from '@/components/appDriver/adsRegistration/types'

  type ErrorKey = 'loadError' | 'submitError' | 'networkError' | 'authError'
  let registration: AdsRegistration | null = null
  let isLoading = true
  let isSubmitting = false
  let errorKey: ErrorKey | null = null
  let isActive = false
  let statusRequest: AbortController | undefined

  // $: registeredDate = registration?.registeredAt
  //   ? new Intl.DateTimeFormat('lo-LA', {
  //     day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Vientiane',
  //   }).format(new Date(registration.registeredAt))
  //   : ''


  $: registeredDate = registration?.registeredAt
    ? new Intl.DateTimeFormat('lo-LA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit', // optional
      hour12: false,     // use true for 12-hour format with AM/PM
      timeZone: 'Asia/Vientiane',
    }).format(new Date(registration.registeredAt))
    : ''



  onMount(() => {
    isActive = true
    $currentPage = 'ads-registration'
    loadStatus()

    return () => {
      isActive = false
      statusRequest?.abort()
    }
  })

  function getErrorKey(error: unknown, fallback: ErrorKey): ErrorKey {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401 || error.response?.status === 403) return 'authError'
      if (!error.response) return 'networkError'
    }
    return fallback
  }

  async function loadStatus() {
    if (statusRequest || isSubmitting) return
    statusRequest = new AbortController()
    isLoading = true
    errorKey = null
    registration = null

    try {
      const result = await getAdsRegistration(statusRequest.signal)
      if (isActive) registration = result
    } catch (error) {
      if (!isActive) return
      errorKey = getErrorKey(error, 'loadError')
      showErrorMessage(text[errorKey])
    } finally {
      statusRequest = undefined
      if (isActive) isLoading = false
    }
  }

  async function registerInterest() {
    if (isLoading || isSubmitting || !registration || registration.registered) return
    isSubmitting = true
    errorKey = null

    try {
      const result = await registerAdsInterest()
      if (isActive) registration = result
    } catch (error) {
      if (!isActive) return
      errorKey = getErrorKey(error, 'submitError')
      if (errorKey === 'authError') registration = null
      showErrorMessage(text[errorKey])
    } finally {
      if (isActive) isSubmitting = false
    }
  }
</script>

<div lang="lo" class="page flex h-full flex-col overflow-hidden" class:bg-slate-50={registration?.registered} class:bg-[#EFEFEF]={!registration?.registered}>
  <div class="relative shrink-0">
    <Navbar showBack={registration?.registered ? false: true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageHome })}/>
    <img
      src={new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}
      width="100"
      class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      alt="LOCA"
    >
  </div>

  <main class="flex-1 overflow-y-auto px-4 pb-6" aria-busy={isLoading || isSubmitting}>
    {#if registration?.registered}
      <header class="mb-6 mt-5 text-center">
        <h1 class="text-2xl font-bold leading-relaxed text-slate-900">{text.registeredTitle}</h1>
        <p class="mt-1 text-base text-slate-500">{text.registeredSubtitle}</p>
      </header>
    {:else}
      <h1 class="my-6 text-center text-xl font-semibold">{text.title}</h1>
    {/if}

    {#if isLoading}
      <div role="status" class="rounded-lg bg-white p-4 shadow">
        <span class="sr-only">{text.loading}</span>
        <div class="animate-pulse space-y-4" aria-hidden="true">
          <div class="h-6 w-2/3 rounded bg-gray-200"></div>
          <div class="h-4 rounded bg-gray-200"></div>
          <div class="h-4 w-5/6 rounded bg-gray-200"></div>
          <div class="h-4 w-3/4 rounded bg-gray-200"></div>
          <div class="h-12 rounded-lg bg-gray-200"></div>
        </div>
      </div>
    {:else if registration?.registered}
      <section aria-labelledby="ads-registration-success" class="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
        <div role="status" class="text-center">
          <svg class="mx-auto mb-3 h-28 w-48" viewBox="0 0 200 120" fill="none" aria-hidden="true">
            <circle cx="100" cy="60" r="46" fill="#DDF3EB"/>
            <circle cx="100" cy="60" r="30" fill="#009461"/>
            <path d="m88 61 9 9 18-21" stroke="white" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
            <g stroke-width="5" stroke-linecap="round">
              <path d="m40 33 4 5M8 61l6 3m155-22 7-7m-20 49 6 4" stroke="#009461"/>
              <path d="m23 89 8-7m123-57 5-4m20 61 6 3" stroke="#A9E2CF"/>
            </g>
          </svg>
          <h2 id="ads-registration-success" class="text-2xl font-bold leading-relaxed text-emerald-700">{text.registered}</h2>
          <p class="mt-3 text-base font-semibold leading-7 text-slate-900">{text.thankYou}</p>
          <p class="mt-3 text-sm leading-6 text-slate-500">{text.confirmation}</p>
        </div>

        <div class="mt-5 border-t border-slate-200 pt-3">
          <div class="flex items-center gap-4 rounded-xl bg-slate-100 p-4">
            <svg class="h-7 w-7 shrink-0 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2"/>
              <path d="M7 3v4m10-4v4M3 11h18"/>
            </svg>
            <div class="min-w-0 border-l border-slate-300 pl-4">
              <p class="text-sm text-slate-500">{text.registeredOn}</p>
              <time class="mt-1 block text-lg font-bold text-slate-900" datetime={registration.registeredAt}>{registeredDate}</time>
            </div>
          </div>
        </div>

<!--        <aside class="mt-3 flex items-start gap-3 rounded-xl bg-emerald-50 p-4">-->
<!--          <svg class="mt-1 h-6 w-6 shrink-0 text-emerald-700" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">-->
<!--            <circle cx="12" cy="12" r="11"/>-->
<!--            <path d="M12 11v6" stroke="white" stroke-width="2" stroke-linecap="round"/>-->
<!--            <circle cx="12" cy="7" r="1.2" fill="white"/>-->
<!--          </svg>-->
<!--          <div>-->
<!--            <h3 class="font-semibold text-emerald-700">{text.additionalInfo}</h3>-->
<!--            <p class="mt-1 text-sm leading-6 text-slate-500">{text.registrationNotice}</p>-->
<!--          </div>-->
<!--        </aside>-->

        <button
          type="button"
          class="mt-4 flex min-h-12 w-full items-center justify-center gap-3 rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
          on:click={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageHome })}
        >
          {text.backHome}
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 12h16m-6-6 6 6-6 6"/>
          </svg>
        </button>
      </section>
    {:else if registration}
      <section class="space-y-5 rounded-lg bg-white p-5 shadow">
        <p class="text-sm leading-6 text-gray-700">{text.description}</p>
        <ul class="list-disc space-y-3 pl-5 text-sm leading-6 text-gray-600">
          <li>{text.interestOnly}</li>
<!--          <li>{text.noGuarantee}</li>-->
          <li>{text.contact}</li>
        </ul>
        {#if errorKey}
          <p role="alert" class="text-sm text-red-700">{text[errorKey]}</p>
        {/if}
        <button
          type="button"
          class="w-full rounded-lg bg-blue-primary p-3 text-center font-semibold text-white disabled:opacity-50"
          disabled={isSubmitting}
          on:click={registerInterest}
        >
          {isSubmitting ? text.submitting : text.register}
        </button>
      </section>
    {:else if errorKey}
      <section class="space-y-4 rounded-lg bg-white p-5 text-center shadow">
        <p role="alert" class="text-sm leading-6 text-gray-700">{text[errorKey]}</p>
        <button type="button" class="w-full rounded-lg bg-blue-primary p-3 font-semibold text-white" on:click={loadStatus}>
          {text.retry}
        </button>
      </section>
    {/if}
  </main>

  {#if isSubmitting}
    <LoadingOverlay message={text.submitting}/>
  {/if}
</div>
